import { CheckboxGroupFormField } from '@/forms/typings';
import { Checkbox } from '../inputs';
import { cn } from '@/libs';
import { uiConfig } from '@/config';
import { useCallback } from 'react';
import { FieldError, useFormField, useFormActions } from '../../core';
import { FormFieldAlertMessage } from './FormFieldAlertMessage';

import './CheckboxGroupPresenter.scss';

type CheckboxGroupPresenterProps = {
  field: CheckboxGroupFormField;
};

export const CheckboxGroupPresenter: React.FC<CheckboxGroupPresenterProps> = ({
  field,
}) => {
  const { setFieldValue } = useFormActions();
  const [formikField] = useFormField(field.name);

  const containerClasses = cn(
    'cg__container',
    uiConfig.grid.maxColumnsSpanClass,
    Object.entries(field.size).map(([key, value]) => ({
      [`${key}:col-span-${value}`]: value !== undefined,
    })),
  );

  const childrenClasses = cn(
    'grid',
    'gap-x-[20px]',
    'gap-y-[10px]',
    uiConfig.grid.columnsClass,
  );

  // Formik's value, not `field.value`, is the selection: a server snapshot can rebuild
  // the field with an older one while Formik keeps the provider's. Not Applicable
  // clears it to ''.
  const selection =
    typeof formikField.value === 'string' ? formikField.value : '';

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = e.target;

      if (name === '') return;

      const selected = new Set(
        field.isSingleChoice && checked ? [] : selection.split(','),
      );

      if (checked) selected.add(name);
      else selected.delete(name);

      const checkedIds = field.fields
        .map(checkbox => checkbox.fieldId)
        .filter(fieldId => selected.has(fieldId));

      field.fields.forEach(checkbox => {
        setFieldValue(checkbox.fieldId, selected.has(checkbox.fieldId));
      });

      setFieldValue(field.name, checkedIds.join(','));
    },
    [field.fields, field.isSingleChoice, field.name, selection, setFieldValue],
  );

  return (
    // `id` is the jump target for this group's row in the validation summary.
    <div className={containerClasses} id={field.name}>
      <h3 className="cg__title">{field.label}</h3>
      {field.alertMessage && (
        <FormFieldAlertMessage alertMessage={field.alertMessage} />
      )}

      <input type="hidden" {...formikField} value={formikField.value as string | undefined} />

      <div className={childrenClasses}>
        {field.fields.map((checkbox, index) => {
          const checkboxItemClasses = cn(
            'cg__checkbox-item',
            uiConfig.grid.maxColumnsSpanClass,
            checkbox.size
              ? Object.entries(checkbox.size).map(([key, value]) => ({
                  [`${key}:col-span-${value}`]: value !== undefined,
                }))
              : {},
          );
          return (
            <div
              className={checkboxItemClasses}
              key={`${checkbox.name}_${index}`}
            >
              <Checkbox
                name={checkbox.name}
                label={checkbox.label}
                onChange={onChange}
                size={field.scale || checkbox.scale}
              />
            </div>
          );
        })}
      </div>

      <FieldError name={field.name} />
    </div>
  );
};
